import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import Editor from '@monaco-editor/react';

import Button from 'components/Button/Button';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from 'components/Drawer/Drawer';
import { cn } from 'components/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';

const TOOLBAR_ICON_BTN_CLASS =
  'flex w-7 h-7 px-3 py-2 justify-center items-center gap-2 rounded-md bg-base-muted text-base-secondaryForeground';
const TOOLTIP_CONTENT_CLASS =
  'rounded-md border border-base-popover bg-base-popover shadow-md';

export const DEFAULT_HEADER_CSS = `{
    ".portal-header": {},
    ".header-logo": {},
    ".player-id": {},
    ".player-name": {},
    ".balance-container": {},
    ".balance-quantity": {},
    ".balance-image": {},
    ".avatar-container": {},
    ".avatar": {},
    ".avatar-frame": {},
    ".login-button": {},
    ".menu-button": {}
}`;

const EDITOR_OPTIONS = {
  minimap: { enabled: false },
  fontSize: 14,
  formatOnPaste: true,
  scrollBeyondLastLine: false,
  formatOnType: true,
  showFoldingControls: 'always' as const
};

function formatJsonString(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

interface EditHeaderCssDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onSave: (value: string) => void;
}

export const EditHeaderCssDrawer: FC<EditHeaderCssDrawerProps> = ({
  open,
  onOpenChange,
  value,
  onSave
}) => {
  const editorRef = useRef<any>(null);
  const monacoRef = useRef<any>(null);
  const decorationsRef = useRef<any>([]);
  const [editorValue, setEditorValue] = useState('');
  const [jsonError, setJsonError] = useState<{ message: string } | null>(null);

  const setEditorMarkers = useCallback(
    (errorInfo: { line: number; column: number; message: string } | null) => {
      const editor = editorRef.current;
      const monaco = monacoRef.current;
      if (!editor || !monaco) return;

      const model = editor.getModel();
      if (!model) return;

      decorationsRef.current = editor.deltaDecorations(
        decorationsRef.current,
        []
      );

      if (!errorInfo) {
        monaco.editor.setModelMarkers(model, 'json', []);
        return;
      }

      const { line, column, message } = errorInfo;

      monaco.editor.setModelMarkers(model, 'json', [
        {
          startLineNumber: line,
          startColumn: column,
          endLineNumber: line,
          endColumn: column + 1,
          message,
          severity: monaco.MarkerSeverity.Error
        }
      ]);
    },
    []
  );

  const validateJson = useCallback(
    (val: string): boolean => {
      try {
        JSON.parse(val);
        setJsonError(null);
        setEditorMarkers(null);
        return true;
      } catch (error) {
        if (error instanceof SyntaxError) {
          const match = error.message.match(/position (\d+)/);
          if (match) {
            const position = parseInt(match[1], 10);
            const lines = val.substring(0, position).split('\n');
            const line = lines.length;
            const column = lines[lines.length - 1].length + 1;
            const message = `Invalid JSON at line ${line}, column ${column}`;
            setJsonError({ message });
            setEditorMarkers({ line, column, message });
          } else {
            setJsonError({ message: 'Invalid JSON format' });
            setEditorMarkers(null);
          }
        } else {
          setJsonError({ message: 'Invalid JSON format' });
          setEditorMarkers(null);
        }
        return false;
      }
    },
    [setEditorMarkers]
  );

  useEffect(() => {
    if (!open) return;
    const initialValue = value ? formatJsonString(value) : DEFAULT_HEADER_CSS;
    setEditorValue(initialValue);
    validateJson(initialValue);
  }, [open, value, validateJson]);

  const handleEditorChange = useCallback(
    (val: string | undefined) => {
      const newValue = val ?? '';
      setEditorValue(newValue);
      validateJson(newValue);
    },
    [validateJson]
  );

  const handleFormat = useCallback(() => {
    editorRef.current?.getAction('editor.action.formatDocument')?.run();
  }, []);

  const handleReset = useCallback(() => {
    setEditorValue(DEFAULT_HEADER_CSS);
    validateJson(DEFAULT_HEADER_CSS);
    editorRef.current?.setValue(DEFAULT_HEADER_CSS);
  }, [validateJson]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSave = useCallback(() => {
    if (!validateJson(editorValue)) return;
    onSave(JSON.stringify(JSON.parse(editorValue)));
    onOpenChange(false);
  }, [editorValue, validateJson, onSave, onOpenChange]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className="flex flex-col gap-0 overflow-hidden bg-white p-0 text-gray-900 w-full !max-w-[600px]">
        <DrawerHeader className="shrink-0 border-b border-base-menuBorder px-8 py-5 text-left">
          <DrawerTitle>Edit Header CSS Payload</DrawerTitle>
        </DrawerHeader>

        <div className="flex-1 min-h-0 flex flex-col p-6">
          <div className="shrink-0 mb-3 flex items-center justify-between">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleFormat}
                  className={TOOLBAR_ICON_BTN_CLASS}
                  data-testid="EditHeaderCss-format"
                >
                  <FontAwesomeIcon
                    icon="fa-solid fa-list-tree"
                    className="text-sm text-base-secondaryForeground"
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="start"
                className={TOOLTIP_CONTENT_CLASS}
              >
                <p className="text-xs">Format</p>
              </TooltipContent>
            </Tooltip>
            <Button
              type="button"
              variant="outline"
              onClick={handleReset}
              data-testid="EditHeaderCss-reset"
            >
              Reset
            </Button>
          </div>
          <div className="flex-1 min-h-0 rounded-lg border border-neutral-200 overflow-hidden">
            <Editor
              height="100%"
              defaultLanguage="json"
              theme="vs"
              value={editorValue}
              onChange={(val) => handleEditorChange(val)}
              onMount={(editor, monaco) => {
                editorRef.current = editor;
                monacoRef.current = monaco;
              }}
              options={EDITOR_OPTIONS}
            />
          </div>
        </div>

        <div className="shrink-0 px-6">
          <p
            className={cn(
              'text-sm break-words',
              jsonError ? 'text-red-500' : 'text-transparent'
            )}
          >
            {jsonError?.message || ''}
          </p>
        </div>

        <DrawerFooter className="shrink-0 flex-row items-center justify-end gap-3 border-t border-base-menuBorder px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            data-testid="EditHeaderCss-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!!jsonError}
            data-testid="EditHeaderCss-save"
          >
            Save
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
